/**
 * Derivations of request identity from the HTTP host, shared VERBATIM by the two sides that must
 * agree on them:
 *
 * - the server (`parseRequest` in `server-functions-shared.server.ts`), from the
 *   `X-Forwarded-Host` / `Host` header — the values it returns are what `dehydrate()` embeds in
 *   the document;
 * - the client (`getRouter`'s placeholder request context in `router.tsx`), from
 *   `window.location.host` — the values a first render sees when it runs before the dehydrated
 *   payload lands.
 *
 * One implementation on purpose: the placeholder is only safe if it equals the dehydrated value
 * BY CONSTRUCTION, and two hand-mirrored copies drift (the client copy once dropped the port that
 * `Host` carries on bare-hostname dev URLs, and once used `location.origin` where the server
 * hard-codes the scheme from its localhost check). Any divergence reopens the first-render race
 * behind the TEC-3689 / TEC-3696 React #418 hydration mismatches.
 *
 * `host` is the full authority component as the header/`location.host` carry it — hostname plus
 * optional port, and a trailing-dot FQDN stays as sent — so both sides transform identical input.
 */

/** The portal is the first dot-separated label of the host (`glovo.worldcoo.com` → `glovo`). */
export function portalIdFromHost(host: string): string {
	return host.split('.')[0] ?? '';
}

/**
 * The absolute origin the server considers itself reachable at. The scheme is derived from the
 * host, not from the connection: behind the ALB the request is plain HTTP even though the public
 * origin is HTTPS, so `parseRequest` has always inferred `http` only for localhost. The client
 * mirrors that inference rather than trusting `location.protocol`, because matching the
 * dehydrated value is the contract — see the module note.
 */
export function baseUrlFromHost(host: string): string {
	return (host.includes('localhost') ? 'http://' : 'https://') + host;
}

/**
 * An absolute URL into the NGO area, for THIS environment.
 *
 * Two things make a plain `/ngos/login` wrong from the marketing site. It is a different HOST — the
 * NGO area is `ngos.worldcoo.com` against the site's `www.worldcoo.com`, and `ngos.localhost:3001`
 * against `new.localhost:3001` — so a same-origin path lands on a page that does not exist. And
 * `/ngos` is not part of the public path at all: the router's input rewrite PREPENDS the host's first
 * label to every pathname, so the route file `/ngos/login` is served at `/login` on that host and
 * linking to `/ngos/login` there would resolve as `/ngos/ngos/login`.
 *
 * So `path` is the public path (`/login`), and the host is this one with its portal label swapped for
 * `ngos` — the same "first label is the portal" rule `portalIdFromHost` reads, applied in reverse,
 * which is what makes this correct in dev, snd and pro without a table of environment hostnames.
 *
 * Falls back to `path` when the base URL is not yet known (the client placeholder before the
 * dehydrated context lands): a same-origin link is wrong, but it is inert rather than pointing at
 * some other environment, and SSR always has the real value.
 */
export function ngoAreaUrl(baseUrl: string, path: string): string {
	let host: string;
	try {
		host = new URL(baseUrl).host;
	} catch {
		return path;
	}
	if (!host) return path;

	// A single-label host (bare `localhost:3001`) has no portal label to replace, so `ngos` is
	// prepended instead of swapped in.
	const [first, ...rest] = host.split('.');
	const ngoHost = rest.length > 0 ? ['ngos', ...rest].join('.') : `ngos.${first}`;
	return `${baseUrlFromHost(ngoHost)}${path}`;
}

/**
 * The locale a locale-prefixed URL carries (`/en/<slug>` → `en`), or undefined when the first
 * path segment is not one of `locales`. Exact, case-sensitive match — the same rule the router's
 * `rewrite.input` applies — so the two consumers cannot disagree on what counts as a prefix.
 *
 * Used by `parseRequest` to make the PATH authoritative for the document locale: TEC-3696 caught
 * production documents whose entire render (shell `<html lang>` included) used the visitor's
 * Accept-Language on a `/en/…` URL, because the path's locale only reached the render through a
 * later router-rewrite mutation that some requests missed. Deriving it here seeds the request
 * context — and the i18n catalog loaded from it — correctly before the router is even built.
 */
export function localeFromPathname<T extends string>(pathname: string, locales: readonly T[]): T | undefined {
	const first = pathname.split('/').find(Boolean);
	return first !== undefined && (locales as readonly string[]).includes(first) ? (first as T) : undefined;
}
